// Email Service for ClubLink TT
// Uses EmailJS for sending emails without a backend
import emailjs from '@emailjs/browser';

// EmailJS Configuration
// To set up: 
// 1. Create free account at https://www.emailjs.com/
// 2. Create an email service (Gmail, Outlook, etc.)
// 3. Create email templates for each notification type
// 4. Replace these IDs with your actual IDs
const EMAILJS_CONFIG = {
  serviceId: 'YOUR_SERVICE_ID',      // Replace with your EmailJS service ID
  publicKey: 'YOUR_PUBLIC_KEY',      // Replace with your EmailJS public key
  templates: {
    registrationApproved: 'template_reg_approved',
    registrationRejected: 'template_reg_rejected',
    newEvent: 'template_new_event',
    eventCancelled: 'template_event_cancelled',
    eventUpdated: 'template_event_updated',
    orderConfirmed: 'template_order_confirmed',
    // Subscription templates
    subscriptionConfirmed: 'template_sub_confirmed',
    subscriptionReminder: 'template_sub_reminder',
    subscriptionPastDue: 'template_sub_pastdue',
    subscriptionSuspended: 'template_sub_suspended'
  }
};

// Initialize EmailJS
emailjs.init(EMAILJS_CONFIG.publicKey);

/**
 * Send registration approved email
 */
export const sendRegistrationApprovedEmail = async (registration, clubName) => {
  try {
    const templateParams = {
      to_email: registration.email,
      to_name: registration.parentFirstName || 'Parent/Guardian',
      player_name: `${registration.playerFirstName} ${registration.playerLastName}`,
      age_group: registration.ageGroup,
      club_name: clubName || 'ClubLink TT',
      message: 'Congratulations! Your player registration has been approved.',
    };

    const response = await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.templates.registrationApproved,
      templateParams
    );
    
    console.log('Registration approved email sent:', response);
    return { success: true, response };
  } catch (error) {
    console.error('Failed to send registration approved email:', error);
    return { success: false, error };
  }
};

/**
 * Send registration rejected email
 */
export const sendRegistrationRejectedEmail = async (registration, clubName, reason = '') => {
  try {
    const templateParams = {
      to_email: registration.email,
      to_name: registration.parentFirstName || 'Parent/Guardian',
      player_name: `${registration.playerFirstName} ${registration.playerLastName}`,
      club_name: clubName || 'ClubLink TT',
      reason: reason || 'Please contact us for more information.',
      message: 'We regret to inform you that your player registration could not be approved at this time.',
    };

    const response = await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.templates.registrationRejected,
      templateParams
    );
    
    console.log('Registration rejected email sent:', response);
    return { success: true, response };
  } catch (error) {
    console.error('Failed to send registration rejected email:', error);
    return { success: false, error };
  }
};

/**
 * Send new event notification email
 */
export const sendNewEventEmail = async (event, recipients, clubName) => {
  const results = [];
  
  for (const recipient of recipients) {
    try {
      const templateParams = {
        to_email: recipient.email,
        to_name: recipient.name || 'Team Member',
        event_title: event.title,
        event_type: event.type,
        event_date: event.date,
        event_time: event.time || 'TBD',
        event_location: event.location || 'TBD',
        team_name: event.teamName || 'All Teams',
        club_name: clubName || 'ClubLink TT',
        message: `A new ${event.type} has been scheduled.`,
      };

      const response = await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templates.newEvent,
        templateParams
      );
      
      results.push({ email: recipient.email, success: true, response });
    } catch (error) {
      console.error(`Failed to send event email to ${recipient.email}:`, error);
      results.push({ email: recipient.email, success: false, error });
    }
  }
  
  console.log('New event emails sent:', results);
  return results;
};

/**
 * Send event cancelled notification email
 */
export const sendEventCancelledEmail = async (event, recipients, clubName) => {
  const results = [];
  
  for (const recipient of recipients) {
    try {
      const templateParams = {
        to_email: recipient.email,
        to_name: recipient.name || 'Team Member',
        event_title: event.title,
        event_type: event.type,
        event_date: event.date,
        event_time: event.time || '',
        club_name: clubName || 'ClubLink TT',
        message: 'This event has been cancelled.',
      };

      const response = await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templates.eventCancelled,
        templateParams
      );
      
      results.push({ email: recipient.email, success: true, response });
    } catch (error) {
      console.error(`Failed to send cancellation email to ${recipient.email}:`, error);
      results.push({ email: recipient.email, success: false, error });
    }
  }
  
  console.log('Event cancelled emails sent:', results);
  return results;
};

/**
 * Send event updated notification email
 */
export const sendEventUpdatedEmail = async (event, changes, recipients, clubName) => {
  const results = [];
  
  for (const recipient of recipients) {
    try {
      const templateParams = {
        to_email: recipient.email,
        to_name: recipient.name || 'Team Member',
        event_title: event.title,
        event_type: event.type,
        event_date: event.date,
        event_time: event.time || 'TBD',
        event_location: event.location || 'TBD',
        changes: changes || 'Event details have been updated.',
        club_name: clubName || 'ClubLink TT',
        message: 'An event you are registered for has been updated.',
      };

      const response = await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templates.eventUpdated,
        templateParams
      );
      
      results.push({ email: recipient.email, success: true, response });
    } catch (error) {
      console.error(`Failed to send update email to ${recipient.email}:`, error);
      results.push({ email: recipient.email, success: false, error });
    }
  }
  
  console.log('Event updated emails sent:', results);
  return results;
};

/**
 * Send order confirmation email
 */
export const sendOrderConfirmationEmail = async (order, clubName) => {
  try {
    // Format order items for email
    const itemsList = order.items.map(item => 
      `${item.name} (${item.size}) x${item.quantity} - TT$ ${item.price * item.quantity}`
    ).join('\n');

    const templateParams = {
      to_email: order.customerEmail,
      to_name: order.customerName,
      order_id: order.id || 'N/A',
      order_items: itemsList,
      subtotal: `TT$ ${order.subtotal}`,
      delivery_fee: `TT$ ${order.deliveryFee || 50}`,
      total: `TT$ ${order.total}`,
      delivery_address: `${order.address}, ${order.city}`,
      club_name: clubName || 'ClubLink TT',
      message: 'Thank you for your order!',
    };

    const response = await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.templates.orderConfirmed,
      templateParams
    );
    
    console.log('Order confirmation email sent:', response);
    return { success: true, response };
  } catch (error) {
    console.error('Failed to send order confirmation email:', error);
    return { success: false, error };
  }
};

/**
 * Send subscription confirmation email
 */
export const sendSubscriptionConfirmationEmail = async (email, clubName, amount, nextPaymentDate) => {
  try {
    const templateParams = {
      to_email: email,
      club_name: clubName,
      amount: `TT$ ${amount}`,
      next_payment_date: nextPaymentDate,
      message: 'Welcome to ClubLink TT! Your subscription is now active.',
    };

    const response = await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.templates.subscriptionConfirmed,
      templateParams
    );
    
    console.log('Subscription confirmation email sent:', response);
    return { success: true, response };
  } catch (error) {
    console.error('Failed to send subscription confirmation email:', error);
    return { success: false, error };
  }
};

/**
 * Send subscription reminder email (3 days before due)
 */
export const sendSubscriptionReminderEmail = async (email, clubName, amount, dueDate) => {
  try {
    const templateParams = {
      to_email: email,
      club_name: clubName,
      amount: `TT$ ${amount}`,
      due_date: dueDate,
      message: 'Your ClubLink TT subscription payment is due soon.',
    };

    const response = await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.templates.subscriptionReminder,
      templateParams
    );
    
    console.log('Subscription reminder email sent:', response);
    return { success: true, response };
  } catch (error) {
    console.error('Failed to send subscription reminder email:', error);
    return { success: false, error };
  }
};

/**
 * Send subscription past due email
 */
export const sendSubscriptionPastDueEmail = async (email, clubName, amount, daysOverdue) => {
  try {
    const templateParams = {
      to_email: email,
      club_name: clubName,
      amount: `TT$ ${amount}`,
      days_overdue: daysOverdue,
      message: 'Your ClubLink TT subscription payment is overdue. Please pay to avoid service interruption.',
    };

    const response = await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.templates.subscriptionPastDue,
      templateParams
    );
    
    console.log('Subscription past due email sent:', response);
    return { success: true, response };
  } catch (error) {
    console.error('Failed to send subscription past due email:', error);
    return { success: false, error };
  }
};

/**
 * Send subscription suspended email
 */
export const sendSubscriptionSuspendedEmail = async (email, clubName) => {
  try {
    const templateParams = {
      to_email: email,
      club_name: clubName,
      message: 'Your ClubLink TT subscription has been suspended due to non-payment. Please renew to restore access.',
    };

    const response = await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.templates.subscriptionSuspended,
      templateParams
    );
    
    console.log('Subscription suspended email sent:', response);
    return { success: true, response };
  } catch (error) {
    console.error('Failed to send subscription suspended email:', error);
    return { success: false, error };
  }
};

/**
 * Check if EmailJS is configured
 */
export const isEmailConfigured = () => {
  return EMAILJS_CONFIG.serviceId !== 'YOUR_SERVICE_ID' && 
         EMAILJS_CONFIG.publicKey !== 'YOUR_PUBLIC_KEY';
};

export default {
  sendRegistrationApprovedEmail,
  sendRegistrationRejectedEmail,
  sendNewEventEmail,
  sendEventCancelledEmail,
  sendEventUpdatedEmail,
  sendOrderConfirmationEmail,
  sendSubscriptionConfirmationEmail,
  sendSubscriptionReminderEmail,
  sendSubscriptionPastDueEmail,
  sendSubscriptionSuspendedEmail,
  isEmailConfigured
};
